import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { faqsData } from './data';
import { pricing } from '@config/pricing';

const FAQSection: React.FC = () => {
    const [activeFaq, setActiveFaq] = useState<number | null>(null);

    const toggleFaq = (index: number) => {
        setActiveFaq(activeFaq === index ? null : index);
    };

    const renderAnswer = (answer: any) => {
        if (typeof answer === 'string') {
            if (answer === 'questions_list') {
                return (
                    <>
                        <p>You can ask about anything related to your life and spiritual journey! Popular topics include:</p>
                        <ul className="list-disc list-inside mt-2 space-y-1">
                            <li><strong>Vedic Astrology:</strong> Birth chart analysis, planetary influences, career guidance</li>
                            <li><strong>Western Astrology:</strong> Psychological insights, personal growth, zodiac guidance</li>
                            <li><strong>Chinese Astrology:</strong> Five Elements, zodiac animals, feng shui guidance</li>
                            <li><strong>KP System:</strong> Sub-lord analysis, precise timing predictions</li>
                            <li><strong>Tarot Readings:</strong> Relationship guidance, life decisions, spiritual insights</li>
                            <li><strong>Numerology:</strong> Life path numbers, personality analysis, lucky numbers</li>
                            <li><strong>Palm Reading:</strong> Life line analysis, personality traits, destiny insights</li>
                            <li><strong>Face Reading:</strong> Character assessment, leadership qualities, relationship compatibility</li>
                            <li><strong>Dream Analysis:</strong> Symbolic meanings, subconscious messages, spiritual guidance</li>
                            <li><strong>Compatibility:</strong> Marriage matching, relationship harmony, Guna Milan</li>
                            <li><strong>Horary Astrology:</strong> Yes/No questions, timing predictions, specific decisions</li>
                        </ul>
                    </>
                );
            }
            if (answer === 'birth_details_list') {
                return (
                    <>
                        <p>It depends on the service you choose:</p>
                        <ul className="list-disc list-inside mt-2 space-y-1">
                            <li><strong>Vedic, Western & Chinese Astrology, KP System & Numerology:</strong> Birth date, time, and place for accurate readings</li>
                            <li><strong>Tarot, Palm Reading, Face Reading:</strong> No birth details required - analysis based on your questions and uploaded images</li>
                            <li><strong>Dream Analysis & Horary Astrology:</strong> Only the question or dream description needed</li>
                            <li><strong>Compatibility Analysis:</strong> Birth details for both people being compared</li>
                        </ul>
                        <p className="mt-2">You can always ask general questions about spiritual guidance without providing personal details. The more information you provide, the more personalized and precise your guidance will be.</p>
                    </>
                );
            }
            if (answer === 'accuracy_list') {
                return (
                    <>
                        <p>Our spiritual guidance is based on authentic ancient wisdom traditions:</p>
                        <ul className="list-disc list-inside mt-2 space-y-1">
                            <li><strong>Vedic Astrology:</strong> Based on Bhrigu Samhita and traditional Jyotish principles</li>
                            <li><strong>Tarot:</strong> Traditional Rider-Waite symbolism and interpretations</li>
                            <li><strong>Numerology:</strong> Pythagorean and Chaldean number systems</li>
                            <li><strong>Palm Reading & Face Reading:</strong> Ancient Hastrekha and Samudrik Shastra</li>
                            <li><strong>Dream Analysis:</strong> Jungian and spiritual symbolism</li>
                        </ul>
                        <p className="mt-2">While we strive for accuracy based on traditional methods, please remember that spiritual guidance is a tool for insight and self-reflection, not definitive predictions. Use these insights as guidance for making informed decisions about your life journey.</p>
                    </>
                );
            }
            if (answer === 'refund_policy') {
                return (
                    <p>
                        We offer a satisfaction guarantee for all our spiritual guidance services. If you're not happy with your experience within the first few messages, please contact our support team. We provide {pricing.currency}{pricing.signupReward} free credits so you can thoroughly explore and test all our services (Vedic astrology, Western & Chinese astrology, KP System, tarot, numerology, palm reading, face reading, dream analysis, compatibility analysis, horary astrology) before making any payments. This allows you to experience the full range of our spiritual guidance platform risk-free. Check our <Link to="/refunds" className="text-yellow-400 hover:text-yellow-300 underline">Refunds Policy</Link> for detailed terms.
                    </p>
                );
            }
            return answer;
        }
        return answer;
    };

    return (
        <section id="faq" className="py-24 px-6 relative z-10">
            <div className="container mx-auto max-w-3xl">
                <h2 className="text-3xl md:text-4xl lg:text-6xl font-black text-center text-white font-serif mb-12">
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400">Questions?</span> We've Got Answers
                </h2>

                <div className="space-y-4">
                    {faqsData.map((f, i) => {
                        const isActive = activeFaq === i;
                        return (
                            <div
                                key={i}
                                className={`rounded-2xl border transition-colors duration-300 overflow-hidden ${isActive ? 'bg-white/[0.12] border-purple-400/50' : 'bg-white/[0.08] border-white/20 hover:border-purple-400/30'}`}
                            >
                                <button
                                    className="w-full flex items-center justify-between p-6 text-left"
                                    onClick={() => toggleFaq(i)}
                                >
                                    <span className={`font-sans text-lg font-medium transition-colors ${isActive ? 'text-white' : 'text-primary-200'}`}>
                                        {f.q}
                                    </span>
                                    <ChevronDown size={20} className={`transition-transform duration-300 ${isActive ? 'rotate-180 text-white' : 'text-primary-400'}`} />
                                </button>

                                {isActive && (
                                    <div
                                    >
                                        <div className="px-6 pb-6 pt-0 text-primary-200/70 font-sans leading-relaxed">
                                            {renderAnswer(f.a)}
                                        </div>
                                    </div>
                                )}

                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default FAQSection;
